import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { EntityLink } from '../../components/shared/EntityLink';
import {
  MapPin, Search, Truck, Tag, Package,
  Loader2,
  CheckCircle2, XCircle, Clock, AlertTriangle,
  Printer, FileText, ExternalLink, X, Navigation, Globe2
} from 'lucide-react';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import { useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';

type Tab = 'shipments' | 'labels' | 'tracking';

const SHIP_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  delivered: 'ok',
  shipped: 'info',
  ready_to_ship: 'warning',
  cancelled: 'error',
  not_delivered: 'error',
};

const LABEL_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  printed: 'ok',
  pending_print: 'warning',
  uploaded: 'info',
  cancelled: 'error',
  error: 'error',
};

const TRACK_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  delivered: 'ok',
  in_transit: 'info',
  accepted: 'info',
  exception: 'error',
  unknown: 'unknown',
};

export default function LogisticsPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('shipments');

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'shipments', label: t('log.shipments'), icon: <Truck size={16} /> },
    { key: 'labels', label: t('log.labels'), icon: <Tag size={16} /> },
    { key: 'tracking', label: t('log.tracking'), icon: <Navigation size={16} /> },
  ];

  const allowedTabs = visibleTabs('logistics', tabs, can);
  const active = pickActiveTab('logistics', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MapPin size={24} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">{t('nav.logistics')}</h1>
      </div>

      <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {active === 'shipments' && <ShipmentsTab />}
      {active === 'labels' && <LabelsTab />}
      {active === 'tracking' && <TrackingTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────
// SHIPMENTS TAB
// ─────────────────────────────────────────────────

function ShipmentsTab() {
  const { t } = useTranslation();
  const canBuyers = useCan()('global.view_buyers');
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('logistics-shipments');
  const [selectedShipment, setSelectedShipment] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/carriers/shipments?${params}`),
        api.get<any>(`/carriers/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load shipments:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const statuses = ['all', 'delivered', 'shipped', 'ready_to_ship', 'cancelled', 'not_delivered'];

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label={t('common.total')} value={Number(stats.total).toLocaleString()} icon={<Truck size={16} />} color="blue" />
          <StatCard label={t('log.delivered')} value={stats.delivered} icon={<CheckCircle2 size={16} />} color="green" />
          <StatCard label={t('log.shipped')} value={stats.shipped} icon={<Package size={16} />} color="cyan" />
          <StatCard label={t('log.readyToShip')} value={stats.ready_to_ship} icon={<Clock size={16} />} color="yellow" />
          <StatCard label={t('log.countries')} value={stats.countries} icon={<Globe2 size={16} />} color="purple" />
          <StatCard label={t('log.last7d')} value={stats.last_7d} icon={<Clock size={16} />} color="emerald" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} shipment, tracking, order, receiver...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>{s === 'all' ? t('log.allStatuses') : s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">Shipment</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.order')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.receiver')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.destination')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.method')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.tracking')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : data.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedShipment(row)}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <EntityLink type="ml_shipment" value={String(row.ml_shipment_id)} />
                </td>
                <td className="px-3 py-3">
                  <EntityLink type="ml_order" value={String(row.ml_order_id)} />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={SHIP_STATUS_MAP[row.status] || 'unknown'} label={row.status?.replace(/_/g, ' ')} />
                </td>
                {/* Nombre y destino del comprador: solo con la llave de datos de compradores. */}
                <td className="px-3 py-3 text-[var(--color-text)] text-xs max-w-[140px] truncate">
                  {canBuyers ? (row.receiver_name || '—') : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs">
                  {canBuyers ? ([row.dest_city_name, row.dest_state_name, row.dest_country_name].filter(Boolean).join(', ') || '—') : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs">
                  {row.shipping_method_name || '—'}
                  {row.logistic_type && <span className="ml-1 text-[var(--color-text-tertiary)]">({row.logistic_type})</span>}
                </td>
                <td className="px-3 py-3">
                  {row.tracking_number ? (
                    <span className="font-mono text-xs text-cyan-500">{row.tracking_number}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {row.date_created ? new Date(row.date_created).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {selectedShipment && <ShipmentDetailModal shipment={selectedShipment} onClose={() => setSelectedShipment(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────
// LABELS TAB
// ─────────────────────────────────────────────────

function LabelsTab() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('logistics-labels');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/labels?${params}`),
        api.get<any>(`/labels/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load labels:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const statuses = ['all', 'printed', 'pending_print', 'uploaded', 'cancelled', 'error'];

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label={t('common.total')} value={stats.total} icon={<Tag size={16} />} color="blue" />
          <StatCard label={t('log.printed')} value={stats.printed} icon={<Printer size={16} />} color="green" />
          <StatCard label={t('log.pendingPrint')} value={stats.pending} icon={<Clock size={16} />} color="yellow" />
          <StatCard label={t('log.uploaded')} value={stats.uploaded} icon={<FileText size={16} />} color="cyan" />
          <StatCard label={t('log.cancelled')} value={stats.cancelled} icon={<XCircle size={16} />} color="red" />
          <StatCard label={t('log.today')} value={stats.today} icon={<Clock size={16} />} color="emerald" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} order, shipment, tracking...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>{s === 'all' ? t('log.allStatuses') : s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">ID</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.order')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">Shipment</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.tracking')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('log.printed')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">PDF</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : data.map((row) => (
              <tr key={row.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                <td className="px-4 py-3 font-mono text-xs text-[var(--color-text)]">{row.id}</td>
                <td className="px-3 py-3">
                  <EntityLink type="ml_order" value={String(row.ml_order_id)} />
                </td>
                <td className="px-3 py-3">
                  <EntityLink type="ml_shipment" value={String(row.ml_shipment_id)} />
                </td>
                <td className="px-3 py-3 font-mono text-xs text-cyan-500">
                  {row.tracking_number || '—'}
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={LABEL_STATUS_MAP[row.status] || 'unknown'} label={row.status?.replace(/_/g, ' ')} />
                </td>
                <td className="px-3 py-3 text-center">
                  {row.printed ? (
                    <CheckCircle2 size={16} className="text-green-500 mx-auto" />
                  ) : (
                    <XCircle size={16} className="text-[var(--color-text-tertiary)] mx-auto" />
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    {row.google_drive_url && (
                      <a href={row.google_drive_url} target="_blank" rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600" title="Google Drive">
                        <ExternalLink size={14} />
                      </a>
                    )}
                    {row.merged_pdf_path && (
                      <span className="text-xs text-[var(--color-text-tertiary)]" title={row.merged_pdf_path}>
                        <FileText size={14} />
                      </span>
                    )}
                    {!row.google_drive_url && !row.merged_pdf_path && '—'}
                  </div>
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────
// TRACKING TAB
// ─────────────────────────────────────────────────

function TrackingTab() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('logistics-tracking');
  const [selectedTracking, setSelectedTracking] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/tracking?${params}`),
        api.get<any>(`/tracking/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load tracking:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const statuses = ['all', 'in_transit', 'accepted', 'delivered', 'exception', 'unknown'];

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label={t('common.total')} value={stats.total} icon={<Navigation size={16} />} color="blue" />
          <StatCard label={t('log.inTransit')} value={stats.in_transit} icon={<Truck size={16} />} color="cyan" />
          <StatCard label={t('log.delivered')} value={stats.delivered} icon={<CheckCircle2 size={16} />} color="green" />
          <StatCard label={t('log.exceptions')} value={stats.exceptions} icon={<AlertTriangle size={16} />} color="red" />
          <StatCard label={t('log.unknown')} value={stats.unknown} icon={<XCircle size={16} />} color="gray" />
        </div>
      )}

      {/* Carrier breakdown */}
      {stats?.carriers && stats.carriers.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {stats.carriers.map((c: any) => (
            <span key={c.carrier_code} className="px-3 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm">
              <span className="font-medium text-[var(--color-text)]">{c.carrier_name || c.carrier_code}</span>
              <span className="ml-2 text-[var(--color-text-tertiary)]">{c.cnt}</span>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-3 items-center">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>{s === 'all' ? t('log.allStatuses') : s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Tracking cards */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</div>
      ) : (
        <div className="space-y-3">
          {data.map((track) => (
            <div
              key={track.id}
              onClick={() => loadTrackingDetail(track.id, setSelectedTracking)}
              className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 hover:border-[var(--color-primary)] cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    track.carrier_code === 'ups' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                    track.carrier_code === 'usps' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                    'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    <Truck size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-[var(--color-text)]">{track.tracking_number}</span>
                      <span className="px-2 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-xs text-[var(--color-text-secondary)] uppercase">
                        {track.carrier_name || track.carrier_code}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-tertiary)]">
                      {track.event_count > 0 && <span>{track.event_count} eventos</span>}
                      {track.last_event_at && <span>{t('log.lastEvent')}: {new Date(track.last_event_at).toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={TRACK_STATUS_MAP[track.status] || 'unknown'} label={track.status_detail || track.status} />
                  {track.tracking_url && (
                    <a href={track.tracking_url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-500 hover:text-blue-600">
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {selectedTracking && <TrackingDetailModal tracking={selectedTracking} onClose={() => setSelectedTracking(null)} />}
    </div>
  );
}

async function loadTrackingDetail(id: number, setTracking: (t: any) => void) {
  try {
    const res = await api.get<any>(`/tracking/${id}`);
    if (res.success && res.data) setTracking(res.data);
  } catch (err) {
    console.error('Failed to load tracking detail:', err);
  }
}

// ─────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────

function StatCard({ label, value, icon, color }: { label: string; value: any; icon: React.ReactNode; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-500 bg-green-50 dark:bg-green-900/20',
    red: 'text-red-500 bg-red-50 dark:bg-red-900/20',
    yellow: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    purple: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
    cyan: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
    emerald: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    gray: 'text-gray-500 bg-gray-50 dark:bg-gray-900/20',
  };

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded ${colorMap[color] || colorMap.gray}`}>{icon}</div>
        <span className="text-xs text-[var(--color-text-tertiary)]">{label}</span>
      </div>
      <div className="text-lg font-bold text-[var(--color-text)]">{value}</div>
    </div>
  );
}

function ShipmentDetailModal({ shipment, onClose }: { shipment: any; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const canBuyers = can('global.view_buyers');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('log.shipmentDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label="Shipment" value={<EntityLink type="ml_shipment" value={String(shipment.ml_shipment_id)} />} />
            <DetailRow label={t('log.order')} value={<EntityLink type="ml_order" value={String(shipment.ml_order_id)} />} />
            <DetailRow label={t('common.status')} value={<StatusBadge status={SHIP_STATUS_MAP[shipment.status] || 'unknown'} label={shipment.status?.replace(/_/g, ' ')} />} />
            {shipment.substatus && <DetailRow label="Sub-status" value={shipment.substatus} />}
            <DetailRow label={t('log.receiver')} value={canBuyers ? (shipment.receiver_name || '—') : '—'} />
            {shipment.receiver_phone && canBuyers && <DetailRow label="Tel" value={shipment.receiver_phone} />}
          </div>

          {/* Dirección de entrega: dato personal del comprador */}
          {canBuyers && (
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <h4 className="text-xs font-medium text-[var(--color-text-secondary)] mb-2 uppercase">{t('log.destination')}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <DetailRow label="Ciudad" value={shipment.dest_city_name || '—'} />
                <DetailRow label="Estado" value={shipment.dest_state_name || '—'} />
                <DetailRow label="País" value={shipment.dest_country_name || '—'} />
                <DetailRow label="CP" value={shipment.dest_zip_code || '—'} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label={t('log.method')} value={shipment.shipping_method_name || '—'} />
            <DetailRow label="Logistic" value={shipment.logistic_type || '—'} />
            {shipment.tracking_number && <DetailRow label={t('log.tracking')} value={shipment.tracking_number} />}
            {shipment.shipping_cost && can('global.view_money') && <DetailRow label="Costo envío" value={`${shipment.shipping_currency_id || ''} ${Number(shipment.shipping_cost).toFixed(2)}`} />}
            {shipment.estimated_delivery_date && <DetailRow label="Entrega est." value={new Date(shipment.estimated_delivery_date).toLocaleDateString()} />}
            {shipment.shipped_at && <DetailRow label="Enviado" value={new Date(shipment.shipped_at).toLocaleString()} />}
            {shipment.weight_g && <DetailRow label="Peso" value={`${(shipment.weight_g / 1000).toFixed(1)} kg`} />}
            {shipment.height_cm && <DetailRow label="Dimensiones" value={`${shipment.height_cm}×${shipment.length_cm}×${shipment.width_cm} cm`} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackingDetailModal({ tracking, onClose }: { tracking: any; onClose: () => void }) {
  const { t } = useTranslation();
  const events = tracking.events || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('log.trackingDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Truck size={24} className="text-[var(--color-primary)]" />
            <div>
              <span className="font-mono text-sm font-medium text-[var(--color-text)]">{tracking.tracking_number}</span>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[var(--color-text-secondary)] uppercase">{tracking.carrier_name}</span>
                <StatusBadge status={TRACK_STATUS_MAP[tracking.status] || 'unknown'} label={tracking.status_detail || tracking.status} />
              </div>
            </div>
            {tracking.tracking_url && (
              <a href={tracking.tracking_url} target="_blank" rel="noopener noreferrer"
                className="ml-auto text-blue-500 hover:text-blue-600">
                <ExternalLink size={18} />
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            {tracking.actual_delivery && <DetailRow label="Entregado" value={new Date(tracking.actual_delivery).toLocaleString()} />}
            {tracking.estimated_delivery && <DetailRow label="Est. entrega" value={new Date(tracking.estimated_delivery).toLocaleDateString()} />}
            {tracking.current_location && <DetailRow label="Ubicación" value={tracking.current_location} />}
            <DetailRow label="Polls" value={tracking.poll_count || 0} />
            {tracking.last_polled_at && <DetailRow label="Último poll" value={new Date(tracking.last_polled_at).toLocaleString()} />}
          </div>

          {/* Event Timeline */}
          {events.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-[var(--color-text-secondary)] mb-3 uppercase">{t('log.events')} ({events.length})</h4>
              <div className="space-y-0">
                {events.map((ev: any, idx: number) => (
                  <div key={ev.id || idx} className="flex gap-3 pb-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                        ev.status === 'delivered' ? 'bg-green-500' :
                        ev.status === 'exception' ? 'bg-red-500' :
                        'bg-blue-400'
                      }`} />
                      {idx < events.length - 1 && <div className="w-0.5 flex-1 bg-[var(--color-border)] mt-1" />}
                    </div>
                    <div className="min-w-0 flex-1 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-[var(--color-text)]">{ev.status}</span>
                        <span className="text-xs text-[var(--color-text-tertiary)]">
                          {ev.event_time ? new Date(ev.event_time).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{ev.description}</p>
                      {ev.location && <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">{ev.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[var(--color-text-tertiary)] text-xs">{label}</span>
      <div className="text-[var(--color-text)] text-sm">{value}</div>
    </div>
  );
}
